<template>
  <AppContainer flush-header>
    <div class="pt-11">
      <EPageTitleMobile
        :title="t(useTranslationPath(callerPath, 'manageEngineers'))"
        description=""
      />

      <div class="space-y-6">
        <template v-for="type in ['active', 'inactive']" :key="type">
          <div v-if="engineersByStatus[type].length">
            <h3 class="text-surface-foreground-deep mb-4 px-4 text-left font-semibold break-words">
              {{
                t(useTranslationPath(callerPath, type === 'active' ? 'workingNow' : 'notWorking'))
              }}
            </h3>
            <EList>
              <EListItem
                v-for="engineer in engineersByStatus[type]"
                :key="`engineer${engineer.id}`"
                :title="engineer.name"
              >
                <template #action>
                  <EButton
                    :label="
                      engineer.status === 'active'
                        ? t(useTranslationPath(callerPath, 'pause'))
                        : t(useTranslationPath(callerPath, 'resume'))
                    "
                    :icon="engineer.status === 'active' ? 'pause' : 'play'"
                    variant="secondary"
                    @click="
                      handleTimeLog(engineer.id, engineer.status === 'active' ? 'pause' : 'resume')
                    "
                  />
                </template>

                <EListItemProperty
                  :label="t(useTranslationPath(callerPath, 'startTime'))"
                  :value="engineer.startTime"
                />
                <EListItemProperty
                  :label="t(useTranslationPath(callerPath, 'endTime'))"
                  :value="engineer.endTime"
                />
                <EListItemProperty
                  :label="t(useTranslationPath(callerPath, 'duration'))"
                  :value="engineer.totalDuration"
                />
              </EListItem>
            </EList>
          </div>
        </template>
      </div>
    </div>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import useDayJs from '@/composables/useDayJs.js'
import { useGoBack } from '@/composables/useGoBack.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { EButton, EList, EListItem, EListItemProperty, EPageTitleMobile } from '@expansivefm/eds'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const appStore = useAppStore()
const engineerStore = useEngineerStore()

const router = useRouter()
const { t } = useI18n()
const { setCustomGoBack } = useGoBack()

const callerPath = 'views.authenticated.workOrders.manageEngineers'

const engineers = computed(() => engineerStore.engineers)

const engineersByStatus = computed(() => {
  return {
    active: engineerStore.engineers.filter((engineer) => engineer.status === 'active'),
    inactive: engineerStore.engineers.filter((engineer) => engineer.status === 'inactive'),
  }
})

onMounted(async () => {
  await fetchEngineers().then(() => {
    setCustomGoBack(() => {
      router.push({
        name: 'work-order.detail',
        params: { workOrderId: engineerStore.session.work_order_id },
      })
    })

    appStore.$patch({ showBackButton: true })
  })
})

const fetchEngineers = async () => {
  return await engineerStore
    .getSession(engineerStore.session.id, {}, ['engineers', 'activeEngineers', 'workOrderTimeLogs'])
    .then(({ data }) => {
      const engineers = data.engineers || []
      const updatedEngineers = []

      engineers.forEach((engineer) => {
        const timeLogs = calculateTimes(
          data.workOrderTimeLogs.filter((log) => log.engineer_id === engineer.id),
        )

        updatedEngineers.push({
          ...engineer,
          status: data.activeEngineers?.find((e) => e.id === engineer.id) ? 'active' : 'inactive',
          ...timeLogs,
        })
      })

      engineerStore.$patch({ engineers: updatedEngineers })
    })
}

const getLastOpenTimeLog = (logs) => {
  const timeLogs = logs.sort((a, b) => {
    return b.start_time > a.start_time ? 1 : -1
  })

  if (timeLogs || timeLogs.length === 0) {
    return null
  }

  return timeLogs[0].end_time_rounded === null ? timeLogs[0] : null
}

const getStartTime = (logs) => {
  const timeLogs = logs.sort((a, b) => {
    return b.start_time > a.start_time ? -1 : 1
  })

  if (timeLogs === undefined || timeLogs.length === 0) {
    return 'N/A'
  }

  return useDayJs(timeLogs[0].start_time_rounded).format('ddd, LT')
}

const getEndTime = (logs) => {
  const timeLogs = logs.sort((a, b) => {
    return b.start_time > a.start_time ? 1 : -1
  })

  if (timeLogs === undefined || timeLogs.length === 0) {
    return 'N/A'
  }

  return timeLogs[0].end_time_rounded === null
    ? 'N/A'
    : useDayJs(timeLogs[0].end_time_rounded).format('ddd, LT')
}

const getTotalDuration = (logs) => {
  let totalDiff = 0

  logs.forEach((log) => {
    totalDiff += useDayJs(log.end_time_rounded ?? undefined).diff(useDayJs(log.start_time_rounded))
  })

  return useDayJs.duration(totalDiff).format('D[d] H[h] m[m]').replace('0d ', '').replace('0h ', '')
}

const calculateTimes = (data) => {
  const startTime = getStartTime(data)
  const endTime = getEndTime(data)
  const totalDuration = getTotalDuration(data)
  const lastOpenTimeLog = getLastOpenTimeLog(data)

  return { startTime, endTime, totalDuration, lastOpenTimeLog }
}

const handleTimeLog = async (engineerId, type) => {
  if (['pause', 'resume'].includes(type)) {
    let activeEngineers = engineers.value
      .filter((e) => e.id !== engineerId)
      .filter((e) => e.status === 'active')
      .map((e) => e.id)

    if (type === 'resume') {
      activeEngineers.push(engineerId)
    }

    engineerStore.updateSession(engineerStore.session.id, { activeEngineers }).then(async () => {
      await fetchEngineers()
    })
  }
}
</script>
